import { Link } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useBadgeVisibility } from '@/context/BadgeVisibilityContext';
import { useSiteContent } from '@/context/SiteContentContext';

export default function Footer() {
  const { theme } = useTheme();
  const { settings } = useBadgeVisibility();
  const { content } = useSiteContent();
  const light = theme === 'light';

  const bg = light ? 'bg-gray-100' : 'bg-[#07101C]';
  const borderColor = light ? 'border-gray-200' : 'border-white/8';
  const headingColor = light ? 'text-gray-900' : 'text-white';
  const mutedColor = light ? 'text-gray-500 hover:text-gray-900' : 'text-white/40 hover:text-white';
  const dimColor = light ? 'text-gray-400' : 'text-white/25';
  const adminColor = light ? 'text-gray-300 hover:text-gray-500' : 'text-white/10 hover:text-white/30';
  const logoFilter = light ? 'brightness-0' : 'brightness-0 invert opacity-70';
  const socialColor = light ? 'text-gray-400 hover:text-gray-700' : 'text-white/30 hover:text-white';
  const dividerInner = light ? 'border-gray-200' : 'border-white/5';
  const socialLinks = [
    { icon: 'ri-facebook-fill', href: content.footer.socials.facebook, label: 'Facebook' },
    { icon: 'ri-instagram-line', href: content.footer.socials.instagram, label: 'Instagram' },
    { icon: 'ri-youtube-line', href: content.footer.socials.youtube, label: 'YouTube' },
    { icon: 'ri-linkedin-box-fill', href: content.footer.socials.linkedin, label: 'LinkedIn' },
  ];

  const columns = [
    {
      heading: 'Products',
      links: [
        { label: 'LiFePO4 Lithium', href: '/products' },
        { label: 'AGM Series', href: '/products' },
        { label: 'Gel Series', href: '/products' },
        { label: 'Compare Batteries', href: '/products/compare' },
        { label: 'Tech Wiki', href: '/tech-wiki' },
      ],
    },
    {
      heading: 'Company',
      links: [
        { label: 'About Stingray', href: '/about' },
        { label: 'Contact Us', href: '/contact' },
        { label: 'FAQ', href: '/faq' },
        { label: 'Dealer Login', href: '/dealer' },
        { label: 'Become a Dealer', href: '/dealer/apply' },
      ],
    },
    {
      heading: 'Legal',
      links: [
        { label: 'Warranty Policy', href: '/warranty' },
        { label: 'Terms of Use', href: '/terms' },
        { label: 'Privacy Policy', href: '/privacy' },
      ],
    },
  ];

  return (
    <footer className={`${bg} transition-colors duration-300`}>
      {/* Main grid */}
      <div className="w-full px-6 md:px-10 pt-14 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">

          {/* Brand column — spans 2 cols on lg */}
          <div className="lg:col-span-2">
            <img
              src="https://static.readdy.ai/image/3bab8804b943b0f9d2e99ce4e4e8123d/a7b2a29c0c21ce8564d3744200077816.png"
              alt="Stingray Marine Batteries"
              className={`h-8 w-auto object-contain mb-5 ${logoFilter}`}
            />
            <p className={`text-xs leading-relaxed max-w-xs mb-6 ${light ? 'text-gray-500' : 'text-white/40'}`}>
              Premium marine batteries engineered for the harshest saltwater environments. LiFePO4, AGM, and Gel — distributed in 50+ countries worldwide.
            </p>

            {/* Contact info */}
            <div className="space-y-2.5 mb-6">
              <a
                href={`tel:${content.footer.phone.replace(/\s+/g, '')}`}
                className={`flex items-center gap-2.5 text-xs transition-colors cursor-pointer group ${mutedColor}`}
              >
                <span className={`w-6 h-6 flex items-center justify-center rounded-md shrink-0 ${light ? 'bg-gray-200' : 'bg-white/5'} group-hover:bg-[var(--accent)] transition-colors`}>
                  <i className="ri-phone-line text-[11px]" style={{ color: 'var(--accent)' }}></i>
                </span>
                {content.footer.phone}
              </a>
              <a
                href={`mailto:${content.footer.email}`}
                className={`flex items-center gap-2.5 text-xs transition-colors cursor-pointer group ${mutedColor}`}
              >
                <span className={`w-6 h-6 flex items-center justify-center rounded-md shrink-0 ${light ? 'bg-gray-200' : 'bg-white/5'} group-hover:bg-[var(--accent)] transition-colors`}>
                  <i className="ri-mail-line text-[11px]" style={{ color: 'var(--accent)' }}></i>
                </span>
                {content.footer.email}
              </a>
              <div className={`flex items-start gap-2.5 text-xs ${light ? 'text-gray-500' : 'text-white/40'}`}>
                <span className={`w-6 h-6 flex items-center justify-center rounded-md shrink-0 mt-0.5 ${light ? 'bg-gray-200' : 'bg-white/5'}`}>
                  <i className="ri-map-pin-line text-[11px]" style={{ color: 'var(--accent)' }}></i>
                </span>
                <span>30 N Gould St Ste R<br />Sheridan, WY 82801, USA</span>
              </div>
            </div>

            {/* Social */}
            <div className="flex flex-wrap items-center gap-2">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  rel="nofollow noopener noreferrer"
                  target="_blank"
                  aria-label={s.label}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all cursor-pointer ${socialColor} ${light ? 'bg-gray-200 hover:bg-gray-300' : 'bg-white/5 hover:bg-white/10'} hover:text-[var(--accent)]`}
                >
                  <i className={`${s.icon} text-sm`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {columns.map((col) => (
            <div key={col.heading}>
              <h3 className={`text-xs font-black uppercase tracking-widest mb-4 ${headingColor}`}>
                {col.heading}
              </h3>
              <ul className="space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link
                      to={l.href}
                      className={`text-xs transition-colors cursor-pointer ${mutedColor}`}
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Warranty badges */}
        <div className={`mt-10 pt-8 border-t ${borderColor}`}>
          {settings.footerWarrantyBadges && (
            <div className="flex flex-wrap gap-3 mb-8">
              {[
                { icon: 'ri-shield-check-line', label: 'LiFePO4', sub: '5-Year Warranty' },
                { icon: 'ri-battery-charge-line', label: 'AGM', sub: '3-Year Warranty' },
                { icon: 'ri-drop-line', label: 'Gel', sub: '2-Year Warranty' },
                { icon: 'ri-global-line', label: '50+ Countries', sub: 'Global Distribution' },
                { icon: 'ri-anchor-line', label: 'Marine Grade', sub: 'IP67 Certified' },
              ].map((badge) => (
                <div
                  key={badge.label}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg ${light ? 'bg-gray-200' : 'bg-white/5'}`}
                >
                  <i className={`${badge.icon} text-sm`} style={{ color: 'var(--accent)' }}></i>
                  <div>
                    <p className={`text-[10px] font-bold ${headingColor}`}>{badge.label}</p>
                    <p className={`text-[9px] ${dimColor}`}>{badge.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Bottom bar */}
          <div className={`flex flex-col sm:flex-row items-center justify-between gap-3 pt-5 border-t ${dividerInner}`}>
            <p className={`text-[11px] text-center sm:text-left ${dimColor}`}>
              &copy; {new Date().getFullYear()} {content.footer.copyright}. All rights reserved.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
              <Link to="/faq" className={`text-[11px] transition-colors cursor-pointer whitespace-nowrap ${mutedColor}`}>FAQ</Link>
              <Link to="/warranty" className={`text-[11px] transition-colors cursor-pointer whitespace-nowrap ${mutedColor}`}>Warranty</Link>
              <Link to="/terms" className={`text-[11px] transition-colors cursor-pointer whitespace-nowrap ${mutedColor}`}>Terms</Link>
              <Link to="/privacy" className={`text-[11px] transition-colors cursor-pointer whitespace-nowrap ${mutedColor}`}>Privacy</Link>
              <Link to="/dealer" className={`text-[11px] transition-colors cursor-pointer whitespace-nowrap ${mutedColor}`}>Dealer Login</Link>
              <Link to="/admin/login" className={`text-[10px] transition-colors cursor-pointer ${adminColor}`}>Admin</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
